import React from "react";

import { formatCurrency, formatKwh, formatTariff, hasValue } from "../shared/formatters";
import { ModalDetailsDivider, ModalDetailsRow } from "../shared/ModalDetailsTable";
import { PriceWithGeedeeDetails } from "../shared/types";

export const PriceWithGeedeeContent = ({
  details,
}: {
  details?: PriceWithGeedeeDetails | null;
}) => {
  const brpDetails = details?.brp_solar_generation_details;
  const lightDetails = details?.light_consumption_and_other_expenses;
  const consumption = lightDetails?.consumption;
  const expenses = lightDetails?.other_expenses;
  const accountTotal = details?.account_total;
  const expenseItems = expenses?.items?.filter((item) => hasValue(item?.amount_brl)) || [];

  const hasBrpDetails =
    hasValue(brpDetails?.gdi?.energy_consumed_kwh) ||
    hasValue(brpDetails?.gdi?.energy_tariff_brl_per_kwh) ||
    hasValue(brpDetails?.gdii?.energy_consumed_kwh) ||
    hasValue(brpDetails?.gdii?.energy_tariff_brl_per_kwh) ||
    hasValue(brpDetails?.total_payable_brp_brl);
  const hasLightConsumption =
    hasValue(consumption?.energy_consumed_kwh) ||
    hasValue(consumption?.energy_tariff_brl_per_kwh) ||
    hasValue(consumption?.total_consumption_brl);
  const hasExpenses =
    hasValue(expenses?.non_compensable_items_brl) ||
    expenseItems.length > 0 ||
    hasValue(expenses?.total_other_expenses_brl);
  const hasLightTotal = hasValue(lightDetails?.total_payable_light_brl);
  const hasAccountTotal = hasValue(accountTotal?.total_amount_brl);

  if (
    !hasBrpDetails &&
    !hasLightConsumption &&
    !hasExpenses &&
    !hasLightTotal &&
    !hasAccountTotal
  ) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-2xl text-sm leading-normal">
      {hasBrpDetails && (
        <>
          <ModalDetailsRow
            label="Energia Consumida em GDI"
            value={formatKwh(brpDetails?.gdi?.energy_consumed_kwh)}
            unit="kWh"
          />
          <ModalDetailsRow
            label="Tarifa de energia BRP"
            currency="R$"
            value={formatTariff(brpDetails?.gdi?.energy_tariff_brl_per_kwh)}
            unit="R$/kWh"
          />

          <div className="mt-5">
            <ModalDetailsRow
              label="Energia Consumida em GDII"
              value={formatKwh(brpDetails?.gdii?.energy_consumed_kwh)}
              unit="kWh"
            />
            <ModalDetailsRow
              label="Tarifa de energia BRP"
              currency="R$"
              value={formatTariff(brpDetails?.gdii?.energy_tariff_brl_per_kwh)}
              unit="R$/kWh"
            />
          </div>

          <ModalDetailsDivider />
          <ModalDetailsRow
            label="Total a pagar BRP"
            currency="R$"
            value={formatCurrency(brpDetails?.total_payable_brp_brl)}
            strong
          />
          <ModalDetailsDivider />
        </>
      )}

      {hasLightConsumption && (
        <div className="mt-5">
          <ModalDetailsRow
            label="Energia Consumida"
            value={formatKwh(consumption?.energy_consumed_kwh)}
            unit="kWh"
          />
          <ModalDetailsRow
            label="Tarifa de energia cativa light"
            currency="R$"
            value={formatTariff(consumption?.energy_tariff_brl_per_kwh)}
            unit="R$/kWh"
          />
          <ModalDetailsRow
            label="Total Consumo"
            currency="R$"
            value={formatCurrency(consumption?.total_consumption_brl)}
            strong
          />
        </div>
      )}

      {hasExpenses && (
        <div className="mt-5">
          <ModalDetailsRow
            label="Itens não-compensáveis (ICMS TUSD, FIO B, ICMS Subvenção)"
            currency="R$"
            value={formatCurrency(expenses?.non_compensable_items_brl)}
          />
          {expenseItems.map((item, index) => (
            <ModalDetailsRow
              key={`${item.description || item.source_description}-${index}`}
              label={item.description || item.source_description || "Despesa"}
              currency="R$"
              value={formatCurrency(item.amount_brl)}
            />
          ))}
          <ModalDetailsRow
            label="Total Outras Despesas"
            currency="R$"
            value={formatCurrency(expenses?.total_other_expenses_brl)}
            strong
          />
        </div>
      )}

      {hasLightTotal && (
        <div className="mt-5">
          <ModalDetailsDivider />
          <ModalDetailsRow
            label="Total a pagar a Light"
            currency="R$"
            value={formatCurrency(lightDetails?.total_payable_light_brl)}
            strong
          />
          <ModalDetailsDivider />
        </div>
      )}

      {hasAccountTotal && (
        <div className="mt-5">
          <ModalDetailsDivider />
          <ModalDetailsRow
            label="Total da Conta"
            currency="R$"
            value={formatCurrency(accountTotal?.total_amount_brl)}
            strong
          />
          <ModalDetailsDivider />
        </div>
      )}
    </div>
  );
};
